import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { formatGBP, relDate } from "../lib/format";
import { toast } from "sonner";
import {
  ChevronLeft, CheckCircle2, Clock, AlertCircle, Shield, Sparkles, ArrowRight,
  Check, X, Handshake, FileCheck2, Eye, EyeOff
} from "lucide-react";

const VERIFIED = {
  verified: { Icon: CheckCircle2, label: "Verified", color: "var(--rag-green-text)", bg: "var(--rag-green-bg)" },
  pending: { Icon: Clock, label: "Pending verification", color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)" },
  incomplete: { Icon: AlertCircle, label: "Incomplete", color: "var(--rag-red-text)", bg: "var(--rag-red-bg)" },
};

const SEV_STYLES = {
  high: { color: "var(--rag-red-text)", bg: "var(--rag-red-bg)", Icon: AlertCircle, label: "High" },
  medium: { color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)", Icon: Clock, label: "Medium" },
  low: { color: "#4F5052", bg: "#EEEEF1", Icon: Eye, label: "Note" },
};

export default function SponsorProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { role } = useRole();

  const [sponsor, setSponsor] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState(params.get("driver_id") || "");
  const [suggestion, setSuggestion] = useState(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [shareIdx, setShareIdx] = useState(null); // null = all, Set = subset
  const [approveOpen, setApproveOpen] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    Promise.all([api.getSponsor(id), api.listDrivers()]).then(([s, d]) => {
      setSponsor(s); setDrivers(d);
    });
  }, [id]);

  useEffect(() => { if (driverId && sponsor) suggest(); /* eslint-disable-next-line */ }, [driverId, sponsor?.id]);

  if (!sponsor) return <Card className="p-8 text-sm text-muted-foreground">Loading sponsor…</Card>;
  const v = VERIFIED[sponsor.verified_status] || VERIFIED.incomplete;
  const c = sponsor.constraints || {};

  const suggest = async () => {
    if (!driverId) return;
    setLoadingMatch(true);
    try {
      const s = await api.suggestMatch(driverId, sponsor.id);
      setSuggestion(s);
      setShareIdx(null);
    } catch (e) {
      toast.error("Could not generate match");
    } finally {
      setLoadingMatch(false);
    }
  };

  const toggleShare = (i, allMedia) => {
    setShareIdx((prev) => {
      const base = prev || new Set(allMedia.map((_, idx) => idx));
      const next = new Set(base);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const approve = async () => {
    try {
      const driver = drivers.find((d) => d.id === driverId);
      const media = driver?.media || [];
      const idxs = shareIdx ? Array.from(shareIdx) : media.map((_, idx) => idx);
      const m = await api.createMatch({
        driver_id: driverId,
        sponsor_id: sponsor.id,
        reviewer_name: role.name,
        reviewer_role: role.label,
        ai_summary: suggestion?.ai_summary,
        shareable_asset_indexes: idxs,
        custom_notes: notes,
      });
      toast.success("Introduction approved", { description: `Match receipt ${m.id} created.` });
      setApproveOpen(false); setNotes("");
      nav(`/matches/${m.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="sponsor-profile-page">
      <Link to="/sponsors" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-sponsors">
        <ChevronLeft className="h-4 w-4" /> Back to sponsors
      </Link>

      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-white">
        <div className="checker-stripe absolute top-0 right-0 w-64 h-64 pointer-events-none" />
        <div className="relative grid lg:grid-cols-5 gap-6 p-6 sm:p-8">
          <div className="lg:col-span-3 flex items-start gap-5">
            <div className="h-20 w-20 rounded-lg grid place-items-center text-white font-semibold text-2xl font-mono-ui shrink-0" style={{ backgroundColor: sponsor.logo_color }}>
              {sponsor.logo_initials}
            </div>
            <div className="min-w-0">
              <div className="tracking-eyebrow text-muted-foreground">{sponsor.type_label}</div>
              <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold mt-1">{sponsor.name}</h1>
              <div className="text-sm text-muted-foreground mt-1">
                {sponsor.rep_name ? <>Contact: {sponsor.rep_name} · {sponsor.rep_role}</> : "Contact details pending"}
              </div>
              {sponsor.terms_summary && <p className="text-[14px] mt-3 max-w-2xl leading-relaxed text-muted-foreground">{sponsor.terms_summary}</p>}
            </div>
          </div>
          <div className="lg:col-span-2 flex flex-col items-start lg:items-end gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs tracking-eyebrow font-mono-ui px-2 py-1 rounded" style={{ color: v.color, backgroundColor: v.bg }}>
              <v.Icon className="h-3.5 w-3.5" /> {v.label}
            </span>
            {sponsor.verified_at && <div className="text-[11px] text-muted-foreground font-mono-ui">Verified {relDate(sponsor.verified_at)}</div>}
            {sponsor.references && sponsor.references.length > 0 && (
              <div className="text-[11px] text-muted-foreground text-right max-w-[260px] mt-1">{sponsor.references[0]}</div>
            )}
          </div>
        </div>
      </section>

      {/* Aims / Support / Constraints */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 border-border race-card">
          <div className="tracking-eyebrow text-muted-foreground">Sponsorship aims</div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(sponsor.aims || []).map((a) => <span key={a} className="text-xs font-mono-ui px-2 py-1 rounded border border-border" data-testid={`aim-${a}`}>{a}</span>)}
          </div>
        </Card>
        <Card className="p-5 border-border race-card">
          <div className="tracking-eyebrow text-muted-foreground">Support offered</div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(sponsor.support_offered || []).map((a) => <span key={a} className="text-xs font-mono-ui px-2 py-1 rounded signal-bg signal-text">{a}</span>)}
          </div>
        </Card>
        <Card className="p-5 border-border race-card">
          <div className="tracking-eyebrow text-muted-foreground">Suitability criteria</div>
          <ul className="text-xs space-y-1.5 mt-3">
            <li><b>Age bands:</b> {(c.age_bands || []).join(", ") || "Any"}</li>
            <li><b>Categories:</b> {(c.categories || []).join(", ") || "Any"}</li>
            <li><b>Geography:</b> {c.geography || "Any"}</li>
            <li><b>Budget range:</b> {c.budget_range_gbp ? `${formatGBP(c.budget_range_gbp[0])}–${formatGBP(c.budget_range_gbp[1])}` : "—"}</li>
            <li><b>Safeguarding:</b> <span className="text-muted-foreground">{c.safeguarding}</span></li>
            {c.reputational_notes && <li><b>Reputational:</b> <span className="text-muted-foreground">{c.reputational_notes}</span></li>}
          </ul>
        </Card>
      </div>

      {/* Match Builder */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Handshake className="h-4 w-4 text-muted-foreground" />
          <div className="tracking-eyebrow text-muted-foreground">Match builder</div>
        </div>
        <Card className="p-6 border-border race-card space-y-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs text-muted-foreground mb-1">Driver</div>
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger data-testid="match-driver-select">
                  <SelectValue placeholder="Pick a driver…" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`match-driver-opt-${d.id}`}>
                      {d.name} · #{d.race_number} · {d.series}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={suggest} disabled={!driverId || loadingMatch} variant="outline" data-testid="redraft-match">
              <Sparkles className="h-4 w-4 mr-1.5" /> {loadingMatch ? "Drafting…" : "Re-suggest"}
            </Button>
          </div>

          {!driverId && (
            <div className="text-sm text-muted-foreground">Pick a driver to see why this sponsor may (or may not) be a fit.</div>
          )}

          {suggestion && (
            <MatchSuggestion
              suggestion={suggestion}
              role={role}
              driver={drivers.find((d) => d.id === driverId)}
              shareIdx={shareIdx}
              onToggleShare={toggleShare}
              onApprove={() => setApproveOpen(true)}
            />
          )}
        </Card>
      </section>

      {/* Past matches */}
      {sponsor.matches && sponsor.matches.length > 0 && (
        <section className="space-y-3">
          <div className="tracking-eyebrow text-muted-foreground">Approved introductions</div>
          <div className="grid md:grid-cols-2 gap-3">
            {sponsor.matches.map((m) => (
              <Link key={m.id} to={`/matches/${m.id}`} className="block" data-testid={`past-match-${m.id}`}>
                <Card className="p-4 border-border race-card hover:shadow-md transition-shadow flex items-center justify-between gap-2">
                  <div className="font-mono-ui">
                    <div className="text-sm">{m.id}</div>
                    <div className="text-[11px] text-muted-foreground">{relDate(m.created_at)} · hash {m.hash}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-match-dialog">
          <DialogHeader>
            <DialogTitle>Approve introduction</DialogTitle>
            <DialogDescription>
              Approving as <b>{role.name}</b> ({role.label}). A match receipt with a shareable read-only link will be created.
              The sponsor sees only the assets you allow.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Optional context (scope of intro, expiry, expectations)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="match-approve-notes"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={approve} data-testid="match-approve-confirm">Approve &amp; create receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MatchSuggestion({ suggestion, role, driver, shareIdx, onToggleShare, onApprove }) {
  const a = suggestion.alignment;
  const ai = suggestion.ai_summary || {};
  const media = driver?.media || [];
  const canApprove = ["club_secretary", "committee"].includes(role.id) && a.ready_to_introduce;
  const scoreColor = a.alignment_score >= 80 ? "var(--rag-green-text)" : a.alignment_score >= 60 ? "var(--rag-amber-text)" : "var(--rag-red-text)";

  return (
    <div className="space-y-5" data-testid="match-suggestion-panel">
      {/* Score + driver glimpse */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-border bg-[#FAFAFB]">
          <div className="flex items-center gap-3">
            <img src={suggestion.driver.avatar_url} alt="" className="h-10 w-10 rounded-md object-cover bg-muted" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{suggestion.driver.name} · #{suggestion.driver.race_number}</div>
              <div className="text-[11px] text-muted-foreground font-mono-ui">{suggestion.driver.series} · {suggestion.driver.age_band}</div>
            </div>
            <div className="text-right">
              <div className="tracking-eyebrow text-muted-foreground">Alignment</div>
              <div className="text-2xl font-semibold font-mono-ui" style={{ color: scoreColor }}>{a.alignment_score}%</div>
            </div>
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden mt-3">
            <div className="h-full rounded-full" style={{ width: `${a.alignment_score}%`, backgroundColor: scoreColor }} />
          </div>
          {ai.why_match && (
            <p className="text-[14px] leading-relaxed mt-4">{ai.why_match}</p>
          )}
        </Card>
        <Card className="p-5 border-border space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 tracking-eyebrow text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5" /> AI alignment
            </div>
            <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border signal-bg signal-text">Advisory</span>
          </div>
          <div className="text-xs space-y-1.5 mt-2">
            <div><b>Confidence:</b> {ai.confidence || "—"}</div>
            <div className="text-muted-foreground">
              {(ai.caveats || []).slice(0, 2).map((c, i) => <div key={i}>· {c}</div>)}
            </div>
            <div className="font-mono-ui text-[10px] text-muted-foreground mt-1">{ai._source}</div>
          </div>
        </Card>
      </div>

      {/* Alignment + flags */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 border-border">
          <div className="tracking-eyebrow text-muted-foreground">Alignment points</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {a.alignment_points.map((p, i) => (
              <li key={i} className="flex gap-2" data-testid={`alignment-point-${i}`}>
                <Check className="h-3.5 w-3.5 mt-0.5 text-[var(--rag-green-text)]" /><span>{p}</span>
              </li>
            ))}
            {a.alignment_points.length === 0 && <li className="text-sm text-muted-foreground">No automatic alignment points yet.</li>}
          </ul>
        </Card>
        <Card className="p-5 border-border">
          <div className="tracking-eyebrow text-muted-foreground">Gaps &amp; risks</div>
          <ul className="mt-2 space-y-2 text-sm">
            {a.flags.length === 0 && <li className="text-sm text-muted-foreground">No automatic flags — verify references manually before introduction.</li>}
            {a.flags.map((f, i) => {
              const s = SEV_STYLES[f.severity] || SEV_STYLES.low;
              return (
                <li key={i} className="flex items-start gap-2" data-testid={`flag-${f.severity}-${i}`}>
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded mt-0.5 shrink-0" style={{ color: s.color, backgroundColor: s.bg }}>
                    <s.Icon className="h-3 w-3" />{s.label}
                  </span>
                  <span>{f.label}</span>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {/* Permissions */}
      {media.length > 0 && (
        <Card className="p-5 border-border">
          <div className="flex items-center justify-between">
            <div className="tracking-eyebrow text-muted-foreground">What can be shared</div>
            <span className="text-[11px] text-muted-foreground">Untick items you don't want included in this introduction.</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {media.map((m, i) => {
              const checked = shareIdx === null ? true : shareIdx.has(i);
              return (
                <label key={i} className="flex items-center gap-3 rounded-md border border-border p-2.5 cursor-pointer" data-testid={`share-perm-${i}`}>
                  <Checkbox checked={checked} onCheckedChange={() => onToggleShare(i, media)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{m.title}</div>
                    <div className="text-[10px] tracking-eyebrow font-mono-ui text-muted-foreground">{m.badge}</div>
                  </div>
                  {checked ? <Eye className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
                </label>
              );
            })}
          </div>
          {ai.shareable_recommendation && (
            <div className="text-[11px] text-muted-foreground mt-3 italic">AI note: {ai.shareable_recommendation}</div>
          )}
          {ai.withhold_recommendation && (
            <div className="text-[11px] text-muted-foreground italic">AI note: {ai.withhold_recommendation}</div>
          )}
        </Card>
      )}

      {/* Approval gate */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button onClick={onApprove} disabled={!canApprove} data-testid="approve-introduction-btn">
          <FileCheck2 className="h-4 w-4 mr-1.5" /> Approve introduction
        </Button>
        {!a.ready_to_introduce && (
          <span className="text-xs text-muted-foreground">
            High-severity gaps must be cleared before an introduction can be approved.
          </span>
        )}
        {a.ready_to_introduce && !["club_secretary", "committee"].includes(role.id) && (
          <span className="text-xs text-muted-foreground">Switch to <b>Club Secretary</b> or <b>Committee</b> to approve.</span>
        )}
      </div>
    </div>
  );
}
